/**
 * BarcodeScanner — обгортка над html5-qrcode.
 *
 * Підтримує EAN-13, UPC-A, Code-128, QR. Використовує задню камеру
 * (facingMode: 'environment') на mobile.
 *
 * Fallback: якщо browser не підтримує getUserMedia або відсутні дозволи,
 * показуємо повідомлення з пропозицією використовувати клавіатурний
 * сканер (USB-HID емулює keyboard input у пошуковий рядок каталогу).
 */
import { useEffect, useRef, useState } from 'react';

interface Props {
  onScan: (decodedText: string) => void;
  onClose: () => void;
}

const SCANNER_ELEM_ID = 'pos-barcode-scanner-region';

export default function BarcodeScanner({ onScan, onClose }: Props) {
  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    let scanner: { stop: () => Promise<void>; clear?: () => void } | null = null;
    let cancelled = false;

    (async () => {
      // Перевірка підтримки getUserMedia
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError(
          'Камера недоступна у цьому браузері. Використовуйте клавіатурний сканер (фокус на полі пошуку каталогу).',
        );
        return;
      }

      try {
        // Динамічний import щоб уникнути SSR/тестових проблем
        const { Html5Qrcode } = await import('html5-qrcode');
        if (cancelled) return;

        const instance = new Html5Qrcode(SCANNER_ELEM_ID);
        scanner = instance;
        await instance.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 240, height: 160 } },
          (decoded) => {
            onScan(decoded);
            // зупиняємось після першого вдалого скану
            instance.stop().catch(() => {});
          },
          () => {
            // ignore decode errors, continue
          },
        );
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        if (msg.includes('NotAllowed') || msg.includes('Permission')) {
          setError('Доступ до камери заборонено. Дозвольте у налаштуваннях браузера.');
        } else if (msg.includes('NotFound')) {
          setError('Камеру не знайдено. Використовуйте клавіатурний сканер.');
        } else {
          setError(`Не вдалося запустити камеру: ${msg}`);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (scanner) {
        scanner.stop().catch(() => {});
      }
    };
  }, [onScan]);

  return (
    <div className="pos-modal-overlay" onClick={onClose}>
      <div className="pos-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pos-modal__header">
          <span>📷 Сканування штрих-коду</span>
          <button className="pos-modal__btn-secondary" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="pos-modal__body">
          {error ? (
            <div className="pos-scanner__error">{error}</div>
          ) : (
            <>
              <div id={SCANNER_ELEM_ID} className="pos-scanner" />
              <div className="pos-scanner__hint">
                Наведіть камеру на штрих-код товару
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
